import { Button } from "@frontend/components/ui/button"
import type { RightModulePanelProps } from "@frontend/pages/case-workspace/radiology/lib/panel-types"
import { AlertCircle, Check, Lock } from "lucide-react"

import { ActiveModulePanel } from "./annotation-panels"
import { ModuleListBar } from "./layout-panels"

export function RightModulePanel({
  modules,
  activeModule,
  onSelectModule,
  moduleCompletions,
  moduleLockById,
  isActiveModuleComplete,
  isActiveModuleEditable,
  activeModuleLockReason,
  onMarkComplete,
  canComplete,
  completionErrors,
  ...activePanelProps
}: RightModulePanelProps) {
  const showErrors = Boolean(completionErrors && completionErrors.length > 0) && !isActiveModuleComplete

  return (
    <>
      <ModuleListBar
        modules={modules}
        activeModule={activeModule}
        onSelectModule={onSelectModule}
        moduleCompletions={moduleCompletions}
        moduleLockById={moduleLockById}
      />

      {activeModule && (
        <div className="px-3 py-2 border-b border-border shrink-0">
          <div className="text-[11px] font-semibold truncate">{activeModule.template_name}</div>
          <div className="text-[10px] text-muted-foreground uppercase">{activeModule.template_type}</div>
        </div>
      )}

      <ActiveModulePanel
        activeModule={activeModule}
        modules={modules}
        isActiveModuleEditable={isActiveModuleEditable}
        {...activePanelProps}
      />

      {activeModuleLockReason && (
        <div className="px-3 py-2 border-t border-border bg-muted/30 shrink-0">
          <div className="text-[10px] text-muted-foreground flex items-start gap-1.5">
            <Lock className="size-3 shrink-0 mt-[1px]" />
            <span>{activeModuleLockReason}</span>
          </div>
        </div>
      )}

      {showErrors && completionErrors && (
        <div className="px-3 py-2 border-t border-border bg-destructive/10 shrink-0">
          <div className="text-[10px] text-destructive flex items-start gap-1.5">
            <AlertCircle className="size-3 shrink-0 mt-[1px]" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-semibold">Cannot mark complete yet:</span>
              <ul className="list-disc pl-4 space-y-0.5">
                {completionErrors.map((msg) => (
                  <li key={msg} className="break-words">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-border p-2.5 shrink-0">
        <Button
          className="w-full h-8 text-[11px]"
          variant={isActiveModuleComplete ? "outline" : "default"}
          disabled={!(canComplete && isActiveModuleEditable)}
          onClick={onMarkComplete}
        >
          <Check className="size-3.5 mr-1.5" />
          {isActiveModuleComplete ? "Mark Incomplete" : "Mark Complete"}
        </Button>
      </div>
    </>
  )
}
